import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, withRouter } from 'react-router-dom';
import Logo from './partials/Logo';
import api from '../../api/http';
import { API_ENDPOINTS } from '../../api/endpoints';
import { CREDIT_UPDATED_EVENT } from "../../utils/creditEvent";
import { isFreeEmailAddress } from '../../utils/emailDomain';
import Modal from '../elements/Modal';
import LanguageToggle from './LanguageToggle';
import { FullTrialModalHost, OPEN_FULL_TRIAL_MODAL } from '../sections/FullTrialModal';
import { OPEN_MOBILE_MENU } from './MobileTabBar';
import { withLanguage } from '../../context/LanguageContext';

import { FaEnvelope, FaCircle, FaStar, FaRegCalendarAlt, FaLifeRing } from 'react-icons/fa';


const propTypes = {
  active: PropTypes.bool,
  navPosition: PropTypes.string,
  hideNav: PropTypes.bool,
  hideSignin: PropTypes.bool,
  bottomOuterDivider: PropTypes.bool,
  bottomDivider: PropTypes.bool,
  // optional: parent can wire this to pricing scroll
  onPricingClick: PropTypes.func,
};

const defaultProps = {
  active: false,
  navPosition: '',
  hideNav: false,
  hideSignin: false,
  bottomOuterDivider: false,
  bottomDivider: false,
  onPricingClick: null,
};

class Header extends React.Component {
  state = {
    isActive: false,
    creditPoints: null,   // license.creditsRemaining
    creditLimit: null,    // license.creditLimit
    creditLoading: false,
    showProfilePopup: false,
    showLogoutModal: false,

    // user / license info
    userEmail: '',
    userLabel: '',        // username/personName/email prefix
    userStatus: '',       // "trial" etc.
    endDate: '',          // subscriptionEndDate / periodEndDate
    daysRemaining: null,  // number of days remaining
  };

  nav = React.createRef();
  hamburger = React.createRef();

  componentDidMount() {
    this.props.active && this.openMenu();
    document.addEventListener('keydown', this.keyPress);
    document.addEventListener('click', this.clickOutside);

    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      this.loadMeData();
    }

    // ✅ LISTEN for credit updates
    window.addEventListener(CREDIT_UPDATED_EVENT, this.loadMeData);
    // Phone bottom bar "Menu" opens the header menu with the account popup.
    window.addEventListener(OPEN_MOBILE_MENU, this.openMobileMenu);
  }

  openMobileMenu = () => {
    this.openMenu();
    this.setState({ showProfilePopup: true });
  };


  componentWillUnmount() {
    window.removeEventListener(OPEN_MOBILE_MENU, this.openMobileMenu);
    document.removeEventListener('keydown', this.keyPress);
    document.removeEventListener('click', this.clickOutside);

    // ✅ CLEAN UP credit listener
    window.removeEventListener(CREDIT_UPDATED_EVENT, this.loadMeData);

    this.closeMenu();
  }


  openMenu = () => {
    document.body.classList.add('off-nav-is-active');
    if (this.nav.current) {
      this.nav.current.style.maxHeight = this.nav.current.scrollHeight + 'px';
    }
    this.setState({ isActive: true });
  };

  closeMenu = () => {
    document.body.classList.remove('off-nav-is-active');
    if (this.nav.current) {
      this.nav.current.style.maxHeight = null;
    }
    this.setState({ isActive: false });
  };

  keyPress = (e) => {
    this.state.isActive && e.keyCode === 27 && this.closeMenu();
  };

  // toggle profile popup
  toggleProfilePopup = () => {
    this.setState((prev) => ({ showProfilePopup: !prev.showProfilePopup }));
  };

  // click outside – close nav + popup
  clickOutside = (e) => {
    if (!this.nav.current) return;

    const isClickInsideNav = this.nav.current.contains(e.target);
    const isClickHamburger =
      this.hamburger.current &&
      (this.hamburger.current === e.target ||
        this.hamburger.current.contains(e.target));

    if (this.state.isActive && !isClickInsideNav && !isClickHamburger) {
      this.closeMenu();
    }

    const isInsideProfile =
      e.target.closest && e.target.closest('.header-profile');

    if (this.state.showProfilePopup && !isInsideProfile) {
      this.setState({ showProfilePopup: false });
    }
  };

  // scroll / navigate to pricing when "プランをアップグレード" is clicked
  handleUpgradeClick = (e) => {
    e.preventDefault();

    this.setState({ showProfilePopup: false });

    // 1) Use callback from parent (same pattern as HeroSplit)
    if (this.props.onPricingClick) {
      this.props.onPricingClick();
      return;
    }

    // 2) Smooth scroll to section with id="pricing" on the same page
    const el = document.getElementById('pricing');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // 3) Fallback: navigate to home and scroll
    try {
      this.props.history.push({ pathname: '/', state: { scrollTarget: 'pricing', requestId: Date.now() } });
    } catch (err) {
      console.error('Pricing navigation failed', err);
    }
  };

  handleInvoiceLinkClick = (e) => {
    e.preventDefault();
    this.setState({ showProfilePopup: false });
    this.closeMenu?.();
    // React-router navigation (you are already using <Link>)
    window.location.href = "/settings/invoice";
  };


  // Load user + credit info from /api/v1/auth/me
  loadMeData = async () => {
    try {
      this.setState({ creditLoading: true });

      const url = API_ENDPOINTS && API_ENDPOINTS.ME;
      const res = await api.get(url);

      const data = res.data?.data || {};
      const user = data.user || {};
      const license = data.license || {};

      // credits
      const creditPoints =
        typeof license.creditsRemaining === 'number'
          ? license.creditsRemaining
          : null;

      const creditLimit =
        typeof license.creditLimit === 'number'
          ? license.creditLimit
          : null;

      // user info
      const userEmail = user.email || '';

      // prefer real username / personName; fallback to email prefix
      const userLabel =
        user.username ||
        user.personName ||
        (userEmail ? userEmail.split('@')[0] : '');

      const userStatus = user.status || license.status || '';

      // subscription end date (or periodEndDate fallback)
      const endDateRaw =
        license.subscriptionEndDate || license.periodEndDate || '';

      let daysRemaining = null;
      if (endDateRaw) {
        const now = new Date();
        const end = new Date(endDateRaw);
        const diffMs = end.getTime() - now.getTime();
        daysRemaining =
          diffMs > 0
            ? Math.ceil(diffMs / (1000 * 60 * 60 * 24))
            : 0;
      }

      this.setState({
        creditPoints,
        creditLimit,
        userEmail,
        userLabel,
        userStatus,
        endDate: endDateRaw,
        daysRemaining,
      });
    } catch (err) {
      console.error('Failed to load /me data:', err);
    } finally {
      this.setState({ creditLoading: false });
    }
  };

  // Logout handler – opens modal
  handleLogout = () => {
    this.setState({ showLogoutModal: true, showProfilePopup: false });
  };

  // Actual logout logic
  confirmLogout = async () => {
    this.setState({ showLogoutModal: false });

    try {
      const refreshToken =
        localStorage.getItem('refreshToken') ||
        sessionStorage.getItem('refreshToken');

      if (refreshToken) {
        await api.post(API_ENDPOINTS.LOGOUT, {
          refreshToken: refreshToken,
          logoutAll: false,
        });
      }
    } catch (err) {
      console.error('Logout API error:', err);
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.clear();

    this.setState({
      creditPoints: null,
      creditLimit: null,
      userEmail: '',
      userLabel: '',
      userStatus: '',
      endDate: '',
      daysRemaining: null,
      showProfilePopup: false,
    });

    this.closeMenu();

    setTimeout(() => {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', () => {
        window.location.replace('/login');
      });
    }, 0);

    window.location.replace('/');
  };

  render() {
    const {
      className,
      navPosition,
      hideNav,
      hideSignin,
      bottomOuterDivider,
      bottomDivider,
      ...props
    } = this.props;

    const {
      creditPoints,
      creditLimit,
      creditLoading,
      showProfilePopup,
      userEmail,
      userLabel,
      userStatus,
      endDate,
      daysRemaining,
    } = this.state;

    const classes = classNames(
      'site-header',
      bottomOuterDivider && 'has-bottom-divider',
      className
    );

    const isLoggedIn = !!localStorage.getItem('accessToken');
    const { language = 'ja', text = (ja) => ja } = this.props.languageContext || {};

    const creditText = creditLoading
      ? '...'
      : creditPoints != null && creditLimit != null
        ? `${creditPoints} / ${creditLimit}`
        : '--';

    const displayLabel = userLabel || text('ユーザー', 'User');

    // Japanese label for current plan based on status
    const planLabel = userStatus;
    // const isEarlyTrial = true;
    const isEarlyTrial = userStatus === 'trial' && creditLimit === 10;

    return (
      <>
      {isLoggedIn && <FullTrialModalHost />}
      <header {...props} className={classes}>
        <div className="container">
          <div
            className={classNames(
              'site-header-inner',
              bottomDivider && 'has-bottom-divider'
            )}
          >
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                // stop React Router navigation
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }

                window.dispatchEvent(new Event("APP_HOME_CLICK"));
              }}
            >
              <Logo />
            </div>
            <div className="language-toggle-slot">
              <LanguageToggle />
            </div>
            {!hideNav && (
              <React.Fragment>
                <button
                  ref={this.hamburger}
                  className="header-nav-toggle"
                  onClick={
                    this.state.isActive ? this.closeMenu : this.openMenu
                  }
                >
                  <span className="screen-reader">Menu</span>
                  <span className="hamburger">
                    <span className="hamburger-inner" />
                  </span>
                </button>
                <nav
                  ref={this.nav}
                  className={classNames(
                    'header-nav',
                    this.state.isActive && 'is-active'
                  )}
                >
                  <div className="header-nav-inner">

                    {!hideSignin && (
                      <ul className="list-reset header-nav-right">
                        {!isLoggedIn ? (
                          <>
                            <li>
                              <Link
                                to="/signup/"
                                className="button button-primary button-wide-mobile button-sm"
                                onClick={this.closeMenu}
                              >
                                {text('サインアップ', 'Sign up')}
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/login/"
                                className="button button-primary button-wide-mobile button-sm"
                                onClick={this.closeMenu}
                              >
                                {text('ログイン', 'Log in')}
                              </Link>
                            </li>
                          </>
                        ) : (
                          <>
                            {/* Credit Points badge BEFORE profile */}
                            <li className="header-credit-points">
                              <div className="credit-pill">
                                <span className="credit-unit">{text('クレジット', 'Credits')}</span>
                                <span className="credit-value">
                                  {creditPoints != null
                                    ? creditPoints.toLocaleString(language === 'en' ? 'en-US' : 'ja-JP')
                                    : '--'}
                                </span>
                              </div>
                            </li>

                            {/* Profile icon + label trigger */}
                            <li className="header-profile">
                              <div
                                className="header-profile-trigger"
                                onClick={this.toggleProfilePopup}
                              >
                                <span className="profile-icon" />
                                <span className="profile-name">
                                  {displayLabel}
                                </span>
                              </div>

                              {showProfilePopup && (
                                <div className="profile-popup">
                                  {/* EMAIL */}
                                  <div className="popup-section popup-email-row">
                                    <div className="popup-email">
                                      {/* <i className="fa-solid fa-envelope" /> */}
                                      <FaEnvelope className="popup-icon" />
                                      <span>{userEmail || text('メール未設定', 'Email not set')}</span>
                                    </div>
                                  </div>

                                  {/* CURRENT PLAN + UPGRADE ROW */}
                                  <div className="popup-section popup-plan-row">
                                    <div className="popup-plan-main">
                                      {/* <i className="fa-solid fa-circle" /> */}
                                      <FaCircle className="popup-icon" />
                                      <span className="popup-plan-text">
                                        {text('現在のプラン', 'Current plan')}: {planLabel}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      className="popup-upgrade-link"
                                      onClick={this.handleUpgradeClick}
                                    >
                                      {text('プランをアップグレード', 'Upgrade plan')}
                                    </button>
                                  </div>
                                  {isEarlyTrial && (
                                    <button
                                      type="button"
                                      className="popup-upgrade-link"
                                      onClick={() => {
                                        this.setState({ showProfilePopup: false });
                                        window.dispatchEvent(new Event(OPEN_FULL_TRIAL_MODAL));
                                      }}
                                    >
                                      {text('フルトライアルを申請する', 'Request full trial')}
                                    </button>
                                  )}
                                  {/* <div className="popup-section popup-bank-row">
                                      <div className="popup-plan-main">
                                        <FaCircle className="popup-icon" />
                                        <span className="popup-plan-text">銀行口座連携</span>
                                      </div>

                                      <Link
                                        to="/settings/bank"
                                        className="popup-upgrade-link"
                                        onClick={() => this.setState({ showProfilePopup: false })}
                                      >
                                        連携する
                                      </Link>
                                    </div> */}

                                  <div className="popup-section popup-bank-row">
                                    <div className="popup-plan-main">
                                      <FaCircle className="popup-icon" />
                                      <span className="popup-plan-text">{text('請求・支払い', 'Billing and payment')}</span>
                                    </div>

                                    <Link
                                      to="/settings/invoice"
                                      className="popup-upgrade-link"
                                      onClick={() => this.setState({ showProfilePopup: false })}
                                    >
                                      {text('連携する', 'Open')}
                                    </Link>
                                  </div>

                                  <div className="popup-section popup-box-row">
                                    <div className="popup-plan-main">
                                      <FaCircle className="popup-icon" />
                                      <span className="popup-plan-text">{text('Box連携', 'Box integration')}</span>
                                    </div>

                                    <Link
                                      to="/settings/box"
                                      className="popup-upgrade-link"
                                      onClick={() => this.setState({ showProfilePopup: false })}
                                    >
                                      {text('設定', 'Settings')}
                                    </Link>
                                  </div>

                                  {!isFreeEmailAddress(this.state.userEmail) && <div className="popup-section popup-box-row">
                                    <div className="popup-plan-main">
                                      <FaCircle className="popup-icon" />
                                      <span className="popup-plan-text">{text('組織・メンバー', 'Organization and members')}</span>
                                    </div>
                                    <Link
                                      to="/settings/organization"
                                      className="popup-upgrade-link"
                                      onClick={() => this.setState({ showProfilePopup: false })}
                                    >
                                      {text('設定', 'Settings')}
                                    </Link>
                                  </div>}

                                  <div className="popup-section popup-box-row">
                                    <div className="popup-plan-main">
                                      <FaLifeRing className="popup-icon" />
                                      <span className="popup-plan-text">{text('サポート', 'Support')}</span>
                                    </div>
                                    <Link
                                      to="/support"
                                      className="popup-upgrade-link"
                                      onClick={() => this.setState({ showProfilePopup: false })}
                                    >
                                      {text('問い合わせ', 'Contact')}
                                    </Link>
                                  </div>

                                  {/* CREDIT */}
                                  <div className="popup-section">
                                    <div className="popup-label">
                                      {/* <i className="fa-solid fa-star" /> */}
                                      <FaStar className="popup-icon" />
                                      {text('クレジット', 'Credits')}
                                    </div>
                                    <div className="popup-value">
                                      {creditText}
                                    </div>
                                  </div>

                                  {/* END DATE + DAYS REMAINING */}
                                  {endDate && (
                                    <div className="popup-section">
                                      <div className="popup-label">
                                        {/* <i className="fa-regular fa-calendar-days" /> */}
                                        <FaRegCalendarAlt className="popup-icon" />
                                        {text('利用期限', 'Expiration date')}
                                      </div>
                                      <div className="popup-value">
                                        {new Date(endDate).toLocaleDateString(language === 'en' ? 'en-US' : 'ja-JP')}
                                        {daysRemaining !== null
                                          ? text(`（残り ${daysRemaining} 日）`, ` (${daysRemaining} days remaining)`)
                                          : ''}
                                      </div>
                                    </div>
                                  )}

                                  <hr />

                                  <button
                                    type="button"
                                    className="button button-logout button-sm popup-logout"
                                    onClick={this.handleLogout}
                                  >
                                    {text('ログアウト', 'Log out')}
                                  </button>
                                </div>
                              )}

                            </li>
                          </>
                        )}
                      </ul>
                    )}
                  </div>
                </nav>
              </React.Fragment>
            )}
          </div>
        </div>

        <Modal
          show={this.state.showLogoutModal}
          handleClose={() => this.setState({ showLogoutModal: false })}
        >
          <div style={{ textAlign: 'center', padding: '10px 20px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1E293B', fontSize: '1.25rem' }}>{text('ログアウトの確認', 'Confirm logout')}</h3>
            <p style={{ marginBottom: '30px', color: '#64748B' }}>{text('本当にログアウトしますか？', 'Are you sure you want to log out?')}</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button
                className="button button-sm"
                onClick={() => this.setState({ showLogoutModal: false })}
                style={{ backgroundColor: '#E2E8F0', color: '#475569', border: 'none' }}
              >
                {text('キャンセル', 'Cancel')}
              </button>
              <button
                className="button button-sm"
                style={{ backgroundColor: 'red', color: '#ffffff', border: 'none' }}
                onClick={this.confirmLogout}
              >
                {text('ログアウト', 'Log out')}
              </button>
            </div>
          </div>
        </Modal>
      </header>
      </>
    );
  }
}

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;

export default withRouter(withLanguage(Header));
